'use client';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { forwardRef, useEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import type { PhoneInputProps, PhoneInputRefType, CountryData } from 'react-international-phone';
import { PhoneInput, defaultCountries } from 'react-international-phone';
import './phone-input-styles.css';

export const PhoneInputField = forwardRef<
  PhoneInputRefType,
  PhoneInputProps & Pick<InputHTMLAttributes<HTMLInputElement>,
    'id' | 'aria-label' | 'aria-labelledby' | 'aria-describedby' | 'aria-invalid' | 'aria-required'> & {
    variant?: 'outlined' | 'default';
  }
>(({ className, variant, id, inputProps, 'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid, 'aria-required': ariaRequired, ...props }, ref) => {
    const [focused, setFocused] = useState(false);
    const phoneInputRef = useRef(null);

    const customCountries = defaultCountries.map(country => {
      if (country[0] === 'Slovakia') {
        return ['Slovakia', 'sk', '421', '... ... ...'] as CountryData;
      }
      return country;
    });

    const locale = useLocaleStore(s => s.currentLocale);

    const defaultCountryFromLocale = () => {
      if (locale === 'cs') {
        return 'cz';
      }

      return locale;
    };

    useEffect(() => {
      const handleClickOutside = (event: any) => {
        const current = phoneInputRef.current as any;

        if (current && !current.contains(event.target)) {
          setFocused(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [phoneInputRef]);

    return (
      <div
        ref={phoneInputRef}
        className={cn(
          'rounded-lg',
          focused ? 'outline outline-1 outline-primary' : 'outline-none',
          variant === 'outlined' ? 'border-1 border border-border' : 'border-none',
          className,
        )}
      >
        <PhoneInput
          ref={ref}
          defaultCountry={defaultCountryFromLocale()}
          name="phone"
          countries={customCountries}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          inputClassName={cn('bg-white')}
          inputStyle={{
            backgroundColor: 'bg-white !important',
            height: '48px !important',
            paddingLeft: '20px',
            width: '100%',
            borderEndEndRadius: 'var(--radius)',
            borderStartEndRadius: 'var(--radius)',
            border: 'none',
          }}
          countrySelectorStyleProps={{
            buttonClassName: 'bg-white',
            buttonStyle: {
              height: '48px !important',
              backgroundColor: 'bg-white !important',
              borderStartStartRadius: 'var(--radius)',
              borderEndStartRadius: 'var(--radius)',
              paddingLeft: '12px',
              border: 'none',
            },
          }}
          {...props}
          inputProps={{
            ...inputProps,
            id: id ?? inputProps?.id,
            'aria-label': ariaLabel ?? inputProps?.['aria-label'],
            'aria-labelledby': ariaLabelledBy ?? inputProps?.['aria-labelledby'],
            'aria-describedby': ariaDescribedBy ?? inputProps?.['aria-describedby'],
            'aria-invalid': ariaInvalid ?? inputProps?.['aria-invalid'],
            'aria-required': ariaRequired ?? inputProps?.['aria-required'],
          }}
        />
      </div>
    );
  });

PhoneInputField.displayName = 'PhoneInputField';
